import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCogs, faExclamationTriangle, faSave } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { SUPER_ADMIN_NAV } from './nav';

interface Config {
    maintenance_mode: boolean; maintenance_message: string; support_email: string;
    support_phone: string; max_upload_size_mb: number; updated_at: string;
}

const SystemConfig: React.FC = () => {
    const { api } = useInternalApi();
    const [config, setConfig] = useState<Config | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => { document.title = 'System Configuration - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('platform/admin/system-configuration/');
            if (res.data?.status === 'success') setConfig(res.data.data);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const save = async () => {
        if (!config) return;
        setSaving(true);
        try {
            const res = await api.patch('platform/admin/system-configuration/', config);
            if (res.data?.status === 'success') {
                Swal.fire({ icon: 'success', title: 'Saved', timer: 1200, showConfirmButton: false });
                setConfig(res.data.data);
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not save configuration.' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <InternalShell activeDashboard="super_admin" subNav={SUPER_ADMIN_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faCogs} className="me-2 text-primary" />System Configuration</h2>
            <p className="text-muted mb-4">Operational settings that take effect immediately across the platform</p>

            {loading || !config ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <div className="card border-0 shadow-sm" style={{ maxWidth: 640 }}>
                    <div className="card-body">
                        {config.maintenance_mode && (
                            <div className="alert alert-danger">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                                <strong>Maintenance mode is currently ON</strong> — every non-Super-Admin request is being blocked with a 503 right now.
                            </div>
                        )}

                        <div className="form-check form-switch mb-3">
                            <input className="form-check-input" type="checkbox" checked={config.maintenance_mode}
                                onChange={(e) => setConfig({ ...config, maintenance_mode: e.target.checked })} />
                            <label className="form-check-label fw-semibold">Maintenance Mode</label>
                            <div className="form-text">Blocks all traffic except Super Admin sessions and `/platform/*` (so you can still log in and turn this back off).</div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Maintenance Message</label>
                            <textarea className="form-control" rows={2} value={config.maintenance_message}
                                onChange={(e) => setConfig({ ...config, maintenance_message: e.target.value })} />
                        </div>

                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <label className="form-label">Support Email</label>
                                <input type="email" className="form-control" value={config.support_email}
                                    onChange={(e) => setConfig({ ...config, support_email: e.target.value })} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Support Phone</label>
                                <input className="form-control" value={config.support_phone}
                                    onChange={(e) => setConfig({ ...config, support_phone: e.target.value })} />
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="form-label">Max Upload Size (MB)</label>
                            <input type="number" className="form-control" style={{ maxWidth: 160 }} value={config.max_upload_size_mb}
                                onChange={(e) => setConfig({ ...config, max_upload_size_mb: Number(e.target.value) })} />
                        </div>

                        <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted small">Last updated {new Date(config.updated_at).toLocaleString()}</span>
                            <button className="btn btn-primary" onClick={save} disabled={saving}>
                                <FontAwesomeIcon icon={faSave} className="me-2" />{saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default SystemConfig;
